import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { Drawer, DrawerBackdrop, DrawerContent } from "../../ui/drawer";
import { useDrawer } from "./hook";
import { DrawerRef, DrawerShowParams } from "./types";

const DrawerRoot = forwardRef(function DrawerRoot(_, ref) {
  const { show, hide, isVisible, options, data, contentProps } = useDrawer();

  // This must use useCallback to ensure the ref doesn't
  // get set to null and then a new ref every render.
  useImperativeHandle(
    ref,
    useCallback(
      () => ({
        show,
        hide,
      }),
      [hide, show],
    ),
  );
  return (
    <Drawer isOpen={isVisible} onClose={hide} {...options}>
      <DrawerBackdrop />
      <DrawerContent {...contentProps}>{data.children}</DrawerContent>
    </Drawer>
  );
});

type DrawerRefObj = {
  current: DrawerRef | null;
};

let refs: DrawerRefObj[] = [];

/**
 * Adds a ref to the end of the array,
 * which will be used to show the toasts until its ref becomes null.
 *
 * @param newRef the new ref, which must be stable for the life of the Toast instance.
 */
function addNewRef(newRef: DrawerRef) {
  refs.push({
    current: newRef,
  });
}

/**
 * Removes the passed in ref from the file-level refs array using a strict equality check.
 *
 * @param oldRef the exact ref object to remove from the refs array.
 */
function removeOldRef(oldRef: DrawerRef | null) {
  refs = refs.filter((r) => r.current !== oldRef);
}

export default function DrawerProvider() {
  const drawerRef = useRef<DrawerRef | null>(null);

  /*
    This must use `useCallback` to ensure the ref doesn't get set
    to null and then a new ref every render.
    Failure to do so will cause whichever Toast *renders or re-renders*
    last to be the instance that is used,
    rather than being the Toast that was *mounted* last.
  */
  const setRef = useCallback((ref: DrawerRef | null) => {
    // Since we know there's a ref, we'll update `refs` to use it.
    if (ref) {
      // store the ref in this toast instance to be able to remove it
      // from the array later when the ref becomes null.
      drawerRef.current = ref;
      addNewRef(ref);
    } else {
      // remove the this toast's ref, wherever it is in the array.
      removeOldRef(drawerRef.current);
    }
  }, []);

  return <DrawerRoot ref={setRef} />;
}

function getRef() {
  const reversePriority = [...refs].reverse();
  const activeRef = reversePriority.find((ref) => ref?.current !== null);
  if (!activeRef) {
    return null;
  }
  return activeRef.current;
}

DrawerProvider.show = (params: DrawerShowParams) => {
  getRef()?.show(params);
};

DrawerProvider.hide = () => {
  getRef()?.hide();
};
